import React from "react";
import * as CheckboxRadix from "@radix-ui/react-checkbox";
import { CheckboxProps as CheckboxRadixProps } from "@radix-ui/react-checkbox";
import classNames from "classnames";

interface CheckboxProps extends CheckboxRadixProps {
  /** Unique ID linking the checkbox to its visible label. Supply it when using label. */
  id?: CheckboxRadixProps["id"];
  /** Visible label. Without one, provide aria-label or aria-labelledby. */
  label?: string;
  /** Initial state for an uncontrolled checkbox. */
  defaultChecked?: CheckboxRadixProps["defaultChecked"];
  /** Current state for a controlled checkbox. Use with onCheckedChange. */
  checked?: CheckboxRadixProps["checked"];
  /** Called when the checked state changes. */
  onCheckedChange?: CheckboxRadixProps["onCheckedChange"];
  /** Prevents users from changing the checkbox. */
  disabled?: CheckboxRadixProps["disabled"];
}

/**
 * A labelled checkbox supporting controlled and uncontrolled state.
 *
 * Built on Radix, so it accepts every Radix checkbox prop (`checked`,
 * `defaultChecked`, `disabled`, `onCheckedChange`, …).
 *
 * Pass an `id` whenever you pass a `label` — the label is tied to the input
 * through `htmlFor`, and without a matching `id` clicking the text does
 * nothing.
 */
const Checkbox = ({ id, label, defaultChecked, ...rest }: CheckboxProps) => {
  return (
    <div className="inline-flex items-center">
      <CheckboxRadix.Root
        className={classNames(
          "outline-non flex h-[20px] w-[20px] appearance-none items-center justify-center border-2 border-[#767683] bg-white",
          "transition-[color,transform,border-color,box-shadow] duration-200",
          "data-[state=checked]:border-cerise data-[state=checked]:bg-cerise",
          "disabled:border-[#CACACC] disabled:bg-[#CACACC] disabled:data-[state=checked]:border-[#CACACC] disabled:data-[state=checked]:bg-[#CACACC]",
          "focus:ring-none focus:outline-none focus-visible:outline-none focus-visible:ring focus-visible:ring-2 focus-visible:ring-inset",
          "data-[state=unchecked]:focus-visible:border-cerise data-[state=checked]:focus-visible:ring-white data-[state=unchecked]:focus-visible:ring-[#767683]"
        )}
        id={id}
        defaultChecked={defaultChecked}
        {...rest}
      >
        <CheckboxRadix.Indicator className="text-white">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M5 9.61111L9 13.5L15 6.5" stroke="white" strokeWidth="2" />
          </svg>
        </CheckboxRadix.Indicator>
      </CheckboxRadix.Root>
      {label && (
        <label
          className={classNames(
            "cursor-pointer pl-[8px] text-[16px] leading-none text-mono-70",
            "transition-[color,transform,border-color] duration-200",
            "hover:text-cerise"
          )}
          htmlFor={id}
        >
          {label}
        </label>
      )}
    </div>
  );
};

export default Checkbox;
